import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CreateExpenseDescriptionFormValues,
  UpdateExpenseDescriptionFormValues,
  createExpenseDescription,
  deleteExpenseDescription,
  getExpenseDescriptions,
  restoreExpenseDescription,
  updateExpenseDescription,
} from "../api/expenseDescriptions";
import { queryClient } from "@/api/queryClient";
import { ExpenseDescription } from "@/types/expenseDescription";
import { ApiResponse, PaginatedApiResponse, PaginationInfo } from "@/types/api";
import { AxiosRequestConfig } from "axios";

interface useExpenseDescriptionMutationOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

export const useGetExpenseDescriptions = (config?: AxiosRequestConfig) => {
  return useQuery<
    PaginatedApiResponse<ExpenseDescription[]>,
    Error,
    { data: ExpenseDescription[]; pagination: PaginationInfo }
  >({
    queryKey: ["expense-descriptions", config?.params],
    queryFn: () => getExpenseDescriptions(config),
    select: (response) => ({
      data: response.data,
      pagination: response.pagination,
    }),
  });
};

export const useGetExpenseDescriptionsOptions = (
  config?: AxiosRequestConfig
) => {
  return useQuery<
    ApiResponse<ExpenseDescription[]>,
    Error,
    ExpenseDescription[]
  >({
    queryKey: ["expense-descriptions-options", config?.params],
    queryFn: () => getExpenseDescriptions(config),
    select: (response) => response.data,
  });
};

export const useCreateExpenseDescription = (
  options?: useExpenseDescriptionMutationOptions<ExpenseDescription>
) => {
  return useMutation({
    mutationFn: (data: CreateExpenseDescriptionFormValues) =>
      createExpenseDescription(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-descriptions"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useUpdateExpenseDescription = (
  options?: useExpenseDescriptionMutationOptions<ExpenseDescription>
) => {
  return useMutation({
    mutationFn: (data: UpdateExpenseDescriptionFormValues) =>
      updateExpenseDescription(data.id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-descriptions"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useDeleteExpenseDescription = (
  options?: useExpenseDescriptionMutationOptions<ExpenseDescription>
) => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteExpenseDescription(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-descriptions"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};

export const useRestoreExpenseDescription = (
  options?: useExpenseDescriptionMutationOptions<ExpenseDescription>
) => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => restoreExpenseDescription(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expense-descriptions"] });
      options?.onSuccess?.(data);
    },
    ...options,
  });
};
